import { LEGAL_DOCUMENTS } from "./legal-content";

export const AGREEMENT_STORAGE_KEY = "betterintentions.pendingPolicyAcceptance";
export const DEFAULT_ACCEPTANCE_SOURCE = "signup_form_web";

export const AGREEMENT_POLICY_CONFIG = Object.freeze([
  {
    policyType: "termsOfService",
    label: "Terms of Service",
    path: LEGAL_DOCUMENTS.termsOfService.path,
    version: LEGAL_DOCUMENTS.termsOfService.version,
    acceptedMetadataKey: "termsAccepted",
    versionMetadataKey: "termsVersion",
  },
  {
    policyType: "privacyPolicy",
    label: "Privacy Policy",
    path: LEGAL_DOCUMENTS.privacyPolicy.path,
    version: LEGAL_DOCUMENTS.privacyPolicy.version,
    acceptedMetadataKey: "privacyAccepted",
    versionMetadataKey: "privacyVersion",
  },
  {
    // Explicit, unbundled opt-in consent to process sensitive personal
    // information (sexual orientation, religion, ethnicity, political views).
    // Several US state laws require specific opt-in consent for sensitive data
    // — it must NOT be folded into the Terms/Privacy acceptance, so the signup
    // UI renders it as its own checkbox. Versioned to the Privacy Policy, which
    // describes the processing.
    policyType: "sensitiveDataConsent",
    label: "Sensitive Data Consent",
    path: LEGAL_DOCUMENTS.privacyPolicy.path,
    version: LEGAL_DOCUMENTS.privacyPolicy.version,
    acceptedMetadataKey: "sensitiveDataConsentAccepted",
    versionMetadataKey: "sensitiveDataConsentVersion",
  },
]);

function getBrowserStorage() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  return window.localStorage;
}

function normalizeBoolean(value) {
  if (typeof value === "boolean") return value;
  return String(value || "").trim().toLowerCase() === "true";
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export function createDefaultAgreementSelections() {
  return AGREEMENT_POLICY_CONFIG.reduce((acc, config) => {
    acc[config.policyType] = false;
    return acc;
  }, {});
}

export function hasRequiredAgreementSelections(selections = {}) {
  return AGREEMENT_POLICY_CONFIG.every((config) => Boolean(selections?.[config.policyType]));
}

export function getAgreementValidationMessage(selections = {}) {
  if (hasRequiredAgreementSelections(selections)) return "";
  return "Please accept the Terms of Service and Privacy Policy, and consent to sensitive data processing, to create your account.";
}

export function buildAgreementClientMetadata(selections = {}, acceptedAt = new Date().toISOString()) {
  return AGREEMENT_POLICY_CONFIG.reduce(
    (acc, config) => {
      acc[config.acceptedMetadataKey] = String(Boolean(selections?.[config.policyType]));
      acc[config.versionMetadataKey] = config.version;
      return acc;
    },
    {
      agreementAcceptedAt: String(acceptedAt || new Date().toISOString()),
    }
  );
}

export function queuePendingPolicyAcceptances({
  selections = {},
  email = "",
  acceptedAt = new Date().toISOString(),
  source = DEFAULT_ACCEPTANCE_SOURCE,
  storage = getBrowserStorage(),
} = {}) {
  if (!storage || !hasRequiredAgreementSelections(selections)) return false;

  const payload = {
    acceptedAt: String(acceptedAt || new Date().toISOString()),
    email: normalizeEmail(email),
    source: String(source || DEFAULT_ACCEPTANCE_SOURCE),
    acceptances: AGREEMENT_POLICY_CONFIG.reduce((acc, config) => {
      acc[config.policyType] = Boolean(selections?.[config.policyType]);
      return acc;
    }, {}),
    versions: AGREEMENT_POLICY_CONFIG.reduce((acc, config) => {
      acc[config.policyType] = config.version;
      return acc;
    }, {}),
  };

  storage.setItem(AGREEMENT_STORAGE_KEY, JSON.stringify(payload));
  return true;
}

export function readPendingPolicyAcceptances(storage = getBrowserStorage()) {
  if (!storage) return null;

  const raw = storage.getItem(AGREEMENT_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingPolicyAcceptances(storage = getBrowserStorage()) {
  if (!storage) return;
  storage.removeItem(AGREEMENT_STORAGE_KEY);
}

export function getPendingPolicyAcceptanceInputs({
  userEmail = "",
  storage = getBrowserStorage(),
} = {}) {
  const pending = readPendingPolicyAcceptances(storage);
  if (!pending) return [];

  const pendingEmail = normalizeEmail(pending?.email);
  const normalizedUserEmail = normalizeEmail(userEmail);
  if (pendingEmail && normalizedUserEmail && pendingEmail !== normalizedUserEmail) {
    return [];
  }

  const acceptedAt = String(pending?.acceptedAt || new Date().toISOString());
  const source = String(pending?.source || DEFAULT_ACCEPTANCE_SOURCE);

  return AGREEMENT_POLICY_CONFIG.reduce((acc, config) => {
    if (!normalizeBoolean(pending?.acceptances?.[config.policyType])) {
      return acc;
    }

    acc.push({
      policyType: config.policyType,
      policyVersion: String(pending?.versions?.[config.policyType] || config.version).trim(),
      acceptedAt,
      acceptanceSource: source,
    });
    return acc;
  }, []);
}

export function clearPendingPolicyAcceptancesForEmail({
  userEmail = "",
  storage = getBrowserStorage(),
} = {}) {
  const pending = readPendingPolicyAcceptances(storage);
  if (!pending) return false;

  const pendingEmail = normalizeEmail(pending?.email);
  const normalizedUserEmail = normalizeEmail(userEmail);
  if (pendingEmail && normalizedUserEmail && pendingEmail !== normalizedUserEmail) {
    return false;
  }

  clearPendingPolicyAcceptances(storage);
  return true;
}
